// src/utils/invoiceDisplay.js
// Centralized display logic for comprobantes (thermal ticket + A4).
// Implements ARCA "Ley de Transparencia Fiscal" for Factura B/C to consumers
// (CF / Monotributo / Exento): unit price must be the FINAL price (IVA incluido),
// and the total must be a single number. IVA breakdown may be shown as an
// informational leyenda only.

/**
 * Map cliente_tipo code → human label.
 * Single source of truth used by ticket + A4.
 */
export const getClientConditionLabel = (code) => {
    const map = {
        CF: 'Consumidor Final',
        RI: 'Responsable Inscripto',
        M: 'Monotributo',
        EX: 'Exento',
    };
    return map[code] || code || 'Consumidor Final';
};

/**
 * Documents where IVA must be discriminated (Neto + IVA = Total).
 * A and NC A only — between Responsables Inscriptos.
 */
export const isDiscriminada = (tipoComprobante) => {
    if (!tipoComprobante) return false;
    const upper = String(tipoComprobante).toUpperCase();
    return upper.includes('FACTURA A') || upper.includes('NOTA DE CREDITO A') || upper.includes('NC A');
};

/**
 * Documents that must show the FINAL unit price (IVA incluido).
 * B / C / Presupuesto / NC B / NC X — Ley de Transparencia Fiscal.
 */
export const shouldShowFinalPrice = (tipoComprobante) => {
    return !isDiscriminada(tipoComprobante);
};

/**
 * Returns the per-unit price that MUST be displayed to the consumer.
 *   - A / NC A → neto (sin IVA)
 *   - B / C / Presupuesto / NC B / NC X → precio final (IVA incluido)
 *
 * @param {number} precioUnitarioSinIva  Stored value in invoice_items (always sin IVA)
 * @param {number} alicuotaIva           21 / 10.5 / 27 (default 21)
 * @param {string} tipoComprobante       FACTURA A | FACTURA B | ...
 */
export const getDisplayUnitPrice = (precioUnitarioSinIva, alicuotaIva = 21, tipoComprobante) => {
    const base = parseFloat(precioUnitarioSinIva) || 0;
    if (isDiscriminada(tipoComprobante)) return base;
    const rate = (parseFloat(alicuotaIva) || 21) / 100;
    return base * (1 + rate);
};

/**
 * Column header label for the unit price column.
 */
export const getUnitPriceLabel = (tipoComprobante) => {
    return isDiscriminada(tipoComprobante)
        ? 'Precio Unit. (Neto)'
        : 'Precio Unit. (IVA incluido)';
};

/**
 * IVA contained in a final (con-IVA) price, using the standard
 * formula: iva = precioConIva - precioConIva / (1 + rate)
 */
export const calcIvaIncluido = (precioConIva, alicuotaIva = 21) => {
    const conIva = parseFloat(precioConIva) || 0;
    const rate = (parseFloat(alicuotaIva) || 21) / 100;
    return conIva - (conIva / (1 + rate));
};

/**
 * Discount amount re-expressed with IVA included (for B/C display).
 * discountSinIva is stored in DB; B/C visual shows discount with IVA.
 */
export const getDiscountConIva = (discountSinIva, alicuotaIva = 21) => {
    const d = parseFloat(discountSinIva) || 0;
    const rate = (parseFloat(alicuotaIva) || 21) / 100;
    return d * (1 + rate);
};